import { useEffect, useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BadgeCheck, KeyRound, Pencil, Shield, Trash2, UserCheck, UserX, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  Badge,
  Button,
  ConfirmDialog,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';
import { Avatar } from '@/components/common/Avatar';
import { SidePanel } from '@/components/common/SidePanel';
import { SectionLoader } from '@/components/common/SectionLoader';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { useRoles } from '@/features/roles/hooks/useRoles';
import { getCompanyColor } from '@/features/shifts/utils/companyColor';
import { usePermissions } from '@/hooks/usePermissions';
import { useOrgCurrency } from '@/hooks/useOrgCurrency';
import type { User, UserCompanySummary } from '@/types';
import {
  useUpdateCompensation,
  useUpdateEmployment,
  useUpdateUser,
  useUser,
} from '../../hooks/useUsers';
import {
  editUserSchema,
  formValuesToCompensationPayload,
  formValuesToEmploymentPayload,
  formValuesToUpdateUserPayload,
  userToEditFormValues,
  type EditUserFormValues,
} from '../../schemas/editUser.schemas';
import { UserDocumentsSection } from '../UserDocumentsSection';
import {
  CompletenessStrip,
  ContactView,
  EmploymentView,
  OverviewView,
  PayView,
} from './ProfileView';
import { ContactEdit, EmploymentEdit, OverviewEdit, PayEdit } from './ProfileEditForm';
import { fieldAccessOf, firstTabWithError, tabsWithErrors, type ProfileTab } from './profileModel';

export type ProfileMode = 'view' | 'edit';

export interface UserProfilePanelProps {
  userId: string;
  /** Companies come from the list row; the single-user endpoint doesn't carry them. */
  companies?: UserCompanySummary[];
  initialMode?: ProfileMode;
  onClose: () => void;
  /** True while a dialog opened from this panel is on top of it. */
  suspendDismiss?: boolean;
  onRoleRequested?: (userId: string) => void;
  onStatusRequested?: (userId: string) => void;
  onResetPasswordRequested?: (user: User) => void;
  onConfirmPermanentRequested?: (user: User) => void;
  onTerminateRequested?: (user: User) => void;
  onDeleteRequested?: (user: User) => void;
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  active: 'success',
  inactive: 'neutral',
  suspended: 'warning',
  exited: 'neutral',
};

const LIFECYCLE_VARIANT: Record<string, BadgeVariant> = {
  probation: 'warning',
  permanent: 'neutral',
  terminated: 'danger',
};

const TABS: Array<{ value: ProfileTab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'employment', label: 'Employment' },
  { value: 'pay', label: 'Pay' },
  { value: 'contact', label: 'Contact' },
  { value: 'documents', label: 'Documents' },
];

function lifecycleOf(user: User): string | undefined {
  const employment = (user.employment ?? {}) as Record<string, unknown>;
  if (typeof employment.employmentStatus === 'string') return employment.employmentStatus;
  if (user.status === 'exited') return 'terminated';
  if (typeof employment.confirmationDate === 'string') return 'permanent';
  if (typeof employment.probationEndDate === 'string') return 'probation';
  return undefined;
}

/**
 * One place to read and change a person's record. Opens over the users list,
 * reads as tabs, and edits in place — the same rows turn into fields — with
 * every account action kept in a fixed footer.
 */
export function UserProfilePanel({
  userId,
  companies = [],
  initialMode = 'view',
  onClose,
  suspendDismiss = false,
  onRoleRequested,
  onStatusRequested,
  onResetPasswordRequested,
  onConfirmPermanentRequested,
  onTerminateRequested,
  onDeleteRequested,
}: UserProfilePanelProps): React.ReactElement {
  const titleId = useId();
  const formId = useId();
  const { data: user, isLoading, error } = useUser(userId);
  const { can } = usePermissions();
  const { data: roles = [] } = useRoles({ enabled: can('roles', 'read') });
  const { currency: orgCurrency } = useOrgCurrency();
  const updateUser = useUpdateUser();
  const updateEmployment = useUpdateEmployment();
  const updateCompensation = useUpdateCompensation();

  const canUpdate = can('users', 'update');
  const canManageRoles = can('users', 'manage_roles');
  const canDeleteUser = can('users', 'delete');
  const canSeeDocuments =
    can('documents', 'read') ||
    can('documents', 'create') ||
    can('documents', 'delete') ||
    canUpdate;

  const [mode, setMode] = useState<ProfileMode>(canUpdate ? initialMode : 'view');
  const [tab, setTab] = useState<ProfileTab>('overview');
  const [confirmDiscard, setConfirmDiscard] = useState<null | 'cancel' | 'close'>(null);

  const form = useForm<EditUserFormValues>({ resolver: zodResolver(editUserSchema) });
  const { isDirty, errors } = form.formState;

  // Load the record into the form, but never over edits in progress.
  useEffect(() => {
    if (user && !form.formState.isDirty) form.reset(userToEditFormValues(user));
  }, [user, form]);

  const isSaving =
    updateUser.isPending || updateEmployment.isPending || updateCompensation.isPending;
  const editing = mode === 'edit';
  const errorTabs = useMemo(() => tabsWithErrors(errors), [errors]);

  const roleDisplay = useMemo(() => {
    if (!user) return '';
    if (user.roleName) return user.roleName;
    return roles.find((role) => role._id === user.roleId)?.name ?? '';
  }, [roles, user]);

  // While editing, the header previews the name and photo being typed.
  const watchedFirst = form.watch('firstName');
  const watchedLast = form.watch('lastName');
  const watchedAvatar = form.watch('avatar');
  const nameParts = editing ? [watchedFirst, watchedLast] : [user?.firstName, user?.lastName];
  const displayName =
    nameParts.filter((part) => part && part.trim()).join(' ') || user?.email || 'User';
  const avatarSrc = (editing ? watchedAvatar : user?.avatar)?.trim() || undefined;

  const startEditing = (focusTab?: ProfileTab): void => {
    if (focusTab) setTab(focusTab);
    setMode('edit');
  };

  const leaveEditing = (): void => {
    if (user) form.reset(userToEditFormValues(user));
    setMode('view');
  };

  const requestCancel = (): void => {
    if (isDirty) setConfirmDiscard('cancel');
    else leaveEditing();
  };

  const requestClose = (): void => {
    if (editing && isDirty) setConfirmDiscard('close');
    else onClose();
  };

  const onSubmit = async (values: EditUserFormValues): Promise<void> => {
    // Profile, employment and compensation live behind separate endpoints. Save in
    // order and stay in edit mode on failure; the mutations toast their own errors.
    try {
      const access = user ? fieldAccessOf(user) : undefined;
      await updateUser.mutateAsync({
        id: userId,
        payload: formValuesToUpdateUserPayload(values, access),
      });
      const employment = formValuesToEmploymentPayload(values);
      if (employment) await updateEmployment.mutateAsync({ id: userId, payload: employment });
      const compensation = formValuesToCompensationPayload(values, access);
      if (compensation) await updateCompensation.mutateAsync({ id: userId, payload: compensation });
      form.reset(values);
      setMode('view');
    } catch {
      // Keep the form open so the person can fix and retry.
    }
  };

  const onInvalid = (): void => {
    const target = firstTabWithError(form.formState.errors);
    if (target) setTab(target);
  };

  const lifecycle = user ? lifecycleOf(user) : undefined;
  const isDeleted = Boolean(user?.isDeleted) || (user?.status as string | undefined) === 'deleted';
  const visibleTabs = TABS.filter((item) => item.value !== 'documents' || canSeeDocuments);

  const renderHeader = (): React.ReactElement => (
    <header className="shrink-0 border-b border-gray-200 px-6 pt-5 dark:border-gray-800">
      <div className="flex items-start gap-4">
        <Avatar
          name={displayName}
          src={avatarSrc}
          size="lg"
          tone="soft"
          className="h-14 w-14 text-lg"
        />
        <div className="min-w-0 flex-1 pt-0.5">
          <h2
            id={titleId}
            className="truncate text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100"
          >
            {displayName}
          </h2>
          {user ? (
            <>
              <p className="truncate text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Badge
                  variant={STATUS_VARIANT[user.status] ?? 'neutral'}
                  dot
                  size="sm"
                  className="capitalize"
                >
                  {isDeleted ? 'Deleted' : user.status}
                </Badge>
                {lifecycle ? (
                  <Badge
                    variant={LIFECYCLE_VARIANT[lifecycle] ?? 'neutral'}
                    size="sm"
                    className="capitalize"
                  >
                    {lifecycle}
                  </Badge>
                ) : null}
                {roleDisplay ? (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                    <Shield className="h-3.5 w-3.5" aria-hidden />
                    {roleDisplay}
                  </span>
                ) : null}
                <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
                  · {user.employeeId}
                </span>
              </div>
            </>
          ) : null}
        </div>
        <button
          type="button"
          onClick={requestClose}
          className="-mr-2 -mt-1 inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          aria-label="Close profile"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {companies.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="Companies">
          {companies.map((company) => (
            <li
              key={company.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-0.5 text-xs text-gray-700 dark:border-gray-700 dark:text-gray-300"
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: getCompanyColor(company.id).dot }}
              />
              {company.name}
            </li>
          ))}
        </ul>
      ) : null}

      <TabsList aria-label="Profile sections" className="-mb-px mt-4 border-b-0 pb-0">
        {visibleTabs.map((item) => (
          <TabsTrigger key={item.value} value={item.value} className="px-3 py-2.5 first:pl-0">
            <span className="inline-flex items-center gap-1.5">
              {item.label}
              {editing && errorTabs.has(item.value) ? (
                <span className="h-1.5 w-1.5 rounded-full bg-danger" aria-label="has errors" />
              ) : null}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>
    </header>
  );

  const renderBody = (): React.ReactElement | null => {
    if (isLoading) return <SectionLoader variant="detail" label="Loading profile" />;
    if (error)
      return <QueryErrorState error={error} size="inline" title="Couldn't load this user" />;
    if (!user) return null;

    const documentsContent = canSeeDocuments ? (
      <TabsContent value="documents" className="pt-0">
        <UserDocumentsSection
          userId={user._id}
          canUpload={can('documents', 'create')}
          canDelete={can('documents', 'delete')}
        />
      </TabsContent>
    ) : null;

    if (editing) {
      return (
        <>
          <form id={formId} onSubmit={form.handleSubmit(onSubmit, onInvalid)} noValidate>
            <TabsContent value="overview" className="pt-0">
              <OverviewEdit form={form} access={fieldAccessOf(user)} />
            </TabsContent>
            <TabsContent value="employment" className="pt-0">
              <EmploymentEdit form={form} />
            </TabsContent>
            <TabsContent value="pay" className="pt-0">
              <PayEdit form={form} orgCurrency={orgCurrency} access={fieldAccessOf(user)} />
            </TabsContent>
            <TabsContent value="contact" className="pt-0">
              <ContactEdit form={form} />
            </TabsContent>
          </form>
          {/* Uploads save on their own and the section has its own <form>, so it
            sits beside the profile form rather than inside it. */}
          {documentsContent}
        </>
      );
    }

    return (
      <>
        <TabsContent value="overview" className="space-y-7 pt-0">
          <CompletenessStrip
            user={user}
            onFill={canUpdate && !isDeleted ? startEditing : undefined}
          />
          <OverviewView user={user} roleDisplay={roleDisplay} />
        </TabsContent>
        <TabsContent value="employment" className="pt-0">
          <EmploymentView user={user} />
        </TabsContent>
        <TabsContent value="pay" className="pt-0">
          <PayView user={user} fallbackCurrency={orgCurrency} />
        </TabsContent>
        <TabsContent value="contact" className="pt-0">
          <ContactView user={user} />
        </TabsContent>
        {documentsContent}
      </>
    );
  };

  const renderFooter = (): React.ReactElement | null => {
    if (!user || isLoading || error) return null;

    if (editing) {
      return (
        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-3 dark:border-gray-800 dark:bg-gray-900/60">
          <p className="text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
            {isDirty ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
                Unsaved changes
              </span>
            ) : (
              'No changes yet'
            )}
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={requestCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" form={formId} loading={isSaving} disabled={!isDirty}>
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </footer>
      );
    }

    if (isDeleted) return null;

    const showTerminate = canDeleteUser && onTerminateRequested && user.status !== 'exited';
    const showDelete = canDeleteUser && onDeleteRequested;
    const dangerButton =
      'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10';

    return (
      <footer className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-gray-200 bg-gray-50 px-6 py-3 dark:border-gray-800 dark:bg-gray-900/60">
        <div className="flex items-center gap-1">
          {showDelete ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={dangerButton}
              onClick={() => onDeleteRequested?.(user)}
            >
              <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
              Delete
            </Button>
          ) : null}
          {showTerminate ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={dangerButton}
              onClick={() => onTerminateRequested?.(user)}
            >
              <UserX className="mr-1.5 h-4 w-4" aria-hidden />
              Terminate
            </Button>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canUpdate && lifecycle === 'probation' && onConfirmPermanentRequested ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onConfirmPermanentRequested(user)}
            >
              <BadgeCheck className="mr-1.5 h-4 w-4" aria-hidden />
              Confirm permanent
            </Button>
          ) : null}
          {canManageRoles && onRoleRequested ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onRoleRequested(user._id)}
            >
              <Shield className="mr-1.5 h-4 w-4" aria-hidden />
              Change role
            </Button>
          ) : null}
          {canUpdate && onStatusRequested ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onStatusRequested(user._id)}
            >
              <UserCheck className="mr-1.5 h-4 w-4" aria-hidden />
              Change status
            </Button>
          ) : null}
          {canUpdate && onResetPasswordRequested ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onResetPasswordRequested(user)}
            >
              <KeyRound className="mr-1.5 h-4 w-4" aria-hidden />
              Reset password
            </Button>
          ) : null}
          {canUpdate ? (
            <Button type="button" size="sm" onClick={() => startEditing()}>
              <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
              Edit profile
            </Button>
          ) : null}
        </div>
      </footer>
    );
  };

  return (
    <SidePanel
      open
      onClose={requestClose}
      labelledBy={titleId}
      suspendDismiss={suspendDismiss || confirmDiscard !== null}
    >
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as ProfileTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        {renderHeader()}
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6',
            editing && 'bg-white dark:bg-gray-900'
          )}
        >
          {renderBody()}
        </div>
        {renderFooter()}
      </Tabs>

      <ConfirmDialog
        open={confirmDiscard !== null}
        onClose={() => setConfirmDiscard(null)}
        onConfirm={() => {
          const reason = confirmDiscard;
          setConfirmDiscard(null);
          leaveEditing();
          if (reason === 'close') onClose();
        }}
        title="Discard changes?"
        message="Your edits to this profile haven't been saved."
        confirmLabel="Discard"
        variant="danger"
      />
    </SidePanel>
  );
}

UserProfilePanel.displayName = 'UserProfilePanel';
